import { storeMoney } from "@libs/platform-wire";
import type {
  FeaturedAccessCardLeadingVisual,
  FeaturedAccessCardShimmer,
} from "./schema";

export const DEFAULT_FEATURED_ACCESS_CARD_ACCENT = "#1478E8";
export const FEATURED_ACCESS_CARD_HEIGHT = 86;
export const FEATURED_ACCESS_CARD_RADIUS = 23.2;
export const FEATURED_ACCESS_CARD_CONTENT_PADDING = 11;
export const FEATURED_ACCESS_CARD_COPY_GAP = 5;
export const FEATURED_ACCESS_CARD_SECONDARY_TEXT_COLOR = "#E4E6E9";
export const FEATURED_ACCESS_CARD_IMAGE_WIDTH_PERCENT = 33;
export const FEATURED_ACCESS_CARD_SEGMENT_SKEW_DEGREES = -24;
export const FEATURED_ACCESS_CARD_TICKET_DIVIDER_PERCENT = 66.67;
export const FEATURED_ACCESS_CARD_TICKET_CUTOUT_RADIUS = 8;
export const FEATURED_ACCESS_CARD_SHIMMER_DURATION_MS = 1_500;
export const FEATURED_ACCESS_CARD_SHIMMER_INTERVAL_MS = 5_000;
export const FEATURED_ACCESS_CARD_SHIMMER_WIDTH_PERCENT = 24;

/**
 * Resolves optional timing overrides into a safe start-to-start animation
 * interval. An interval shorter than the sweep is raised to the sweep length.
 */
export function resolveFeaturedAccessCardShimmer(
  shimmer?: FeaturedAccessCardShimmer,
) {
  const durationMs =
    shimmer?.durationMs ?? FEATURED_ACCESS_CARD_SHIMMER_DURATION_MS;
  const intervalMs = Math.max(
    durationMs,
    shimmer?.intervalMs ?? FEATURED_ACCESS_CARD_SHIMMER_INTERVAL_MS,
  );

  return { durationMs, intervalMs } as const;
}

/**
 * Find the largest hundredth-scale that fits, with at most eight measurements.
 * Font metrics need not scale linearly, so measure each candidate rather than
 * deriving the next scale from a previous fit.
 */
export function fitFeaturedAccessCardValueScale({
  availableWidth,
  measureWidth,
}: {
  availableWidth: number;
  measureWidth: (scale: number) => number;
}) {
  if (availableWidth <= 0 || measureWidth(1) <= availableWidth) return 1;

  let fitting = 0;
  let tooWide = 100;
  while (tooWide - fitting > 1) {
    const candidate = Math.floor((fitting + tooWide) / 2);
    if (measureWidth(candidate / 100) <= availableWidth) fitting = candidate;
    else tooWide = candidate;
  }
  return fitting / 100;
}

const COMPACT_COUNTRY_NAMES: Readonly<Record<string, string>> = {
  "united arab emirates": "UAE",
  "united kingdom": "UK",
  "united kingdom of great britain and northern ireland": "UK",
  "united states": "US",
  "united states of america": "US",
};

/** Formats the compact venue location consistently across card renderers. */
export function formatFeaturedAccessCardLocation(
  city: string,
  country: string,
) {
  const normalizedCity = city.trim();
  const normalizedCountry = country.trim();
  const compactCountry =
    COMPACT_COUNTRY_NAMES[normalizedCountry.toLocaleLowerCase()] ??
    normalizedCountry;

  return [normalizedCity, compactCountry].filter(Boolean).join(", ");
}

const EVENT_MONTHS = [
  "JAN",
  "FEB",
  "MAR",
  "APR",
  "MAY",
  "JUN",
  "JUL",
  "AUG",
  "SEP",
  "OCT",
  "NOV",
  "DEC",
] as const;

/** Converts a venue-local YYYY-MM-DD value into the card's date tile. */
export function createFeaturedAccessCardEventDate(
  date: string,
  currentYear = new Date().getFullYear(),
): FeaturedAccessCardLeadingVisual | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return undefined;

  const month = EVENT_MONTHS[Number(match[2]) - 1];
  const day = Number(match[3]);
  if (!month || !Number.isInteger(day) || day < 1 || day > 31) return undefined;

  const year = Number(match[1]);
  return {
    type: "event-date",
    month,
    day: String(day),
    ...(year > currentYear ? { year: String(year) } : {}),
  };
}

const REFERENCE_IMAGE_WIDTH_PERCENT = 33;
const REFERENCE_RIBBON_START_PERCENT = 27;
const REFERENCE_VISIBLE_RIBBON_WIDTHS = [5, 31, 15, 22] as const;
const REFERENCE_VISIBLE_RIBBON_TOTAL = 73;
const RIBBON_OVERLAP_PERCENT = 1;
const END_RIBBON_OVERSCAN_PERCENT = 7;

/**
 * Builds the entire composition from the image-layer width. The first ribbon
 * starts underneath the image so its skew creates the visible diagonal crop;
 * every ribbon after that scales into the horizontal space that remains.
 */
export function createFeaturedAccessCardGeometry(
  imageWidthPercent = FEATURED_ACCESS_CARD_IMAGE_WIDTH_PERCENT,
) {
  const ribbonStartPercent =
    imageWidthPercent *
    (REFERENCE_RIBBON_START_PERCENT / REFERENCE_IMAGE_WIDTH_PERCENT);
  const availableRibbonWidthPercent = 100 - ribbonStartPercent;
  const [narrow, main, secondary, end] = REFERENCE_VISIBLE_RIBBON_WIDTHS.map(
    (width) =>
      availableRibbonWidthPercent * (width / REFERENCE_VISIBLE_RIBBON_TOTAL),
  );
  const mainStart = ribbonStartPercent + narrow;
  const secondaryStart = mainStart + main;
  const endStart = secondaryStart + secondary;

  return {
    imageWidthPercent,
    segments: [
      {
        leftPercent: ribbonStartPercent,
        widthPercent: narrow + RIBBON_OVERLAP_PERCENT,
        paletteIndex: 0,
      },
      {
        leftPercent: mainStart,
        widthPercent: main + RIBBON_OVERLAP_PERCENT,
        paletteIndex: 1,
      },
      {
        leftPercent: secondaryStart,
        widthPercent: secondary + RIBBON_OVERLAP_PERCENT,
        paletteIndex: 2,
      },
      {
        leftPercent: endStart,
        widthPercent: end + END_RIBBON_OVERSCAN_PERCENT,
        paletteIndex: 3,
      },
    ] as const,
  };
}

export const FEATURED_ACCESS_CARD_GEOMETRY = createFeaturedAccessCardGeometry();

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

type Rgb = readonly [red: number, green: number, blue: number];
type Hsl = readonly [hue: number, saturation: number, lightness: number];

export type FeaturedAccessCardGradient = Readonly<{
  colors: readonly [string, string, ...string[]];
  locations: readonly [number, number, ...number[]];
}>;

function hexToRgb(hex: string): Rgb {
  const value = hex.slice(1);
  return [
    Number.parseInt(value.slice(0, 2), 16),
    Number.parseInt(value.slice(2, 4), 16),
    Number.parseInt(value.slice(4, 6), 16),
  ];
}

function rgbToHex([red, green, blue]: Rgb): string {
  return `#${[red, green, blue]
    .map((channel) => Math.round(channel).toString(16).padStart(2, "0"))
    .join("")}`.toUpperCase();
}

function hexToRgba(hex: string, alpha: number) {
  const [red, green, blue] = hexToRgb(hex);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

function rgbToHsl([redValue, greenValue, blueValue]: Rgb): Hsl {
  const [red, green, blue] = [redValue, greenValue, blueValue].map(
    (channel) => channel / 255,
  );
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;
  const lightness = (max + min) / 2;

  if (delta === 0) return [0, 0, lightness];

  const saturation = delta / (1 - Math.abs(2 * lightness - 1));
  let hue: number;

  if (max === red) hue = 60 * (((green - blue) / delta) % 6);
  else if (max === green) hue = 60 * ((blue - red) / delta + 2);
  else hue = 60 * ((red - green) / delta + 4);

  return [(hue + 360) % 360, saturation, lightness];
}

function hslToRgb([hue, saturation, lightness]: Hsl): Rgb {
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const hueSection = hue / 60;
  const secondary = chroma * (1 - Math.abs((hueSection % 2) - 1));
  let color: Rgb;

  if (hueSection < 1) color = [chroma, secondary, 0];
  else if (hueSection < 2) color = [secondary, chroma, 0];
  else if (hueSection < 3) color = [0, chroma, secondary];
  else if (hueSection < 4) color = [0, secondary, chroma];
  else if (hueSection < 5) color = [secondary, 0, chroma];
  else color = [chroma, 0, secondary];

  const match = lightness - chroma / 2;
  return color.map((channel) => (channel + match) * 255) as unknown as Rgb;
}

const clampUnit = (value: number) => Math.min(1, Math.max(0, value));

const REFERENCE_ACCENT_HSL = rgbToHsl(
  hexToRgb(DEFAULT_FEATURED_ACCESS_CARD_ACCENT),
);
const SATURATION_VARIATION_STRENGTH = 0.25;
const COOL_ACCENT_LIGHTNESS_VARIATION_STRENGTH = 1.5;

const REFERENCE_GRADIENTS = [
  {
    colors: ["#1E5FD2", "#1F62D6"],
    locations: [0, 1],
  },
  {
    colors: ["#3673DB", "#3A80E9", "#3A7CE5"],
    locations: [0, 0.7, 1],
  },
  {
    colors: ["#246CE1", "#2562D7"],
    locations: [0, 1],
  },
  {
    colors: ["#326AD8", "#335ECB"],
    locations: [0, 1],
  },
] as const satisfies readonly FeaturedAccessCardGradient[];

/** Dark accent-derived surface used behind the event month/day tile. */
export function createFeaturedAccessCardDateTileGradient(accentColor: string) {
  const accent = HEX_COLOR.test(accentColor)
    ? accentColor.toUpperCase()
    : DEFAULT_FEATURED_ACCESS_CARD_ACCENT;
  const [hue, saturation, lightness] = rgbToHsl(hexToRgb(accent));

  const start = rgbToHex(
    hslToRgb([hue, clampUnit(saturation * 0.92), clampUnit(lightness * 0.58)]),
  );
  const end = rgbToHex(
    hslToRgb([hue, clampUnit(saturation * 0.85), clampUnit(lightness * 0.4)]),
  );

  return [hexToRgba(start, 0.9), hexToRgba(end, 0.9)] as const;
}

/** Accent-derived foregrounds for the white CTA surface. */
export function createFeaturedAccessCardActionColors(accentColor: string) {
  const accent = HEX_COLOR.test(accentColor)
    ? accentColor.toUpperCase()
    : DEFAULT_FEATURED_ACCESS_CARD_ACCENT;

  return {
    value: accent,
    previousValue: hexToRgba(accent, 0.8),
  } as const;
}

/** A ticket price, short, on web and native alike: in the house style every
 *  price is said in (`storeMoney`), without decimals when it is whole ("£20",
 *  "£18.50", "NZD 65", "AUS$45"), and "FREE" for nothing to pay. */
export function formatFeaturedAccessCardPrice(
  amount: number,
  currency: string,
) {
  if (amount === 0) return "FREE";
  return storeMoney(amount, currency, { compact: true });
}

function applyReferenceVariation(accentHsl: Hsl, referenceColor: string) {
  const referenceHsl = rgbToHsl(hexToRgb(referenceColor));
  const lightnessVariationStrength =
    accentHsl[0] >= 180 && accentHsl[0] <= 250
      ? COOL_ACCENT_LIGHTNESS_VARIATION_STRENGTH
      : 1;

  return rgbToHex(
    hslToRgb([
      accentHsl[0],
      clampUnit(
        accentHsl[1] +
          (referenceHsl[1] - REFERENCE_ACCENT_HSL[1]) *
            SATURATION_VARIATION_STRENGTH,
      ),
      clampUnit(
        accentHsl[2] +
          (referenceHsl[2] - REFERENCE_ACCENT_HSL[2]) *
            lightnessVariationStrength,
      ),
    ]),
  );
}

/**
 * Recolors the measured reference gradients from one user-controlled accent.
 * Each stop keeps the reference lightness curve while retaining the selected
 * accent's hue and most of its saturation. This preserves the ribbon rhythm
 * without grey or muddy results for warm accent colors.
 */
export function createFeaturedAccessCardGradients(accentColor: string) {
  const accent = HEX_COLOR.test(accentColor)
    ? accentColor.toUpperCase()
    : DEFAULT_FEATURED_ACCESS_CARD_ACCENT;
  const accentHsl = rgbToHsl(hexToRgb(accent));

  return REFERENCE_GRADIENTS.map(
    ({ colors, locations }): FeaturedAccessCardGradient => ({
      colors: colors.map((color) =>
        applyReferenceVariation(accentHsl, color),
      ) as unknown as FeaturedAccessCardGradient["colors"],
      locations,
    }),
  );
}
